import React, { useState, useRef, useEffect, useMemo } from 'react';

// Case- and accent-insensitive match helper for the search bar.
const normalize = (s) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/**
 * Product filter for the chat header: a funnel button opening a panel with a
 * search bar and a checkbox grid of the brand's main products. Retrieval only
 * uses the selected products; by default everything is selected.
 */
function ProductFilter({ products, selectedProducts, onSelectionChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const panelRef = useRef(null);

  // Close the panel when clicking outside (same pattern as the language dropdown)
  useEffect(() => {
    function handleClickOutside(event) {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [panelRef]);

  const visibleProducts = useMemo(() => {
    const needle = normalize(search.trim());
    if (!needle) return products;
    return products.filter((p) => normalize(p).includes(needle));
  }, [products, search]);

  if (!products || products.length === 0) return null;

  const isFiltered = selectedProducts.size > 0 && selectedProducts.size < products.length;

  const toggleProduct = (product) => {
    const next = new Set(selectedProducts);
    if (next.has(product)) {
      next.delete(product);
    } else {
      next.add(product);
    }
    onSelectionChange(next);
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
        aria-label="Filtrer par produit"
        title="Filtrer par produit"
      >
        {/* Funnel icon */}
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          className={`w-5 h-5 ${isFiltered ? 'text-blue-600' : 'text-gray-600'}`}>
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
        </svg>
        {isFiltered && (
          <span className="absolute -top-1 -right-1 min-w-[1.1rem] h-[1.1rem] px-1 flex items-center justify-center rounded-full bg-blue-600 text-white text-[0.65rem] font-semibold">
            {selectedProducts.size}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-2 w-[min(24rem,calc(100vw-2rem))] bg-white rounded-md shadow-xl z-20 border border-gray-200 p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-800">Produits</span>
            <span className="text-xs text-gray-500">
              {selectedProducts.size}/{products.length} sélectionné{selectedProducts.size > 1 ? 's' : ''}
            </span>
          </div>

          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un produit..."
            className="w-full mb-2 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={() => onSelectionChange(new Set(products))}
              className="text-xs text-blue-600 hover:underline"
            >
              Tout sélectionner
            </button>
            <button
              onClick={() => onSelectionChange(new Set())}
              className="text-xs text-blue-600 hover:underline"
            >
              Tout désélectionner
            </button>
          </div>

          <div className="grid grid-cols-2 gap-1 max-h-64 overflow-y-auto">
            {visibleProducts.map((product) => (
              <label
                key={product}
                title={product}
                className="flex items-center gap-2 px-2 py-1 text-sm text-gray-800 rounded hover:bg-gray-100 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedProducts.has(product)}
                  onChange={() => toggleProduct(product)}
                  className="w-4 h-4 shrink-0 accent-blue-600"
                />
                <span className="truncate">{product}</span>
              </label>
            ))}
            {visibleProducts.length === 0 && (
              <p className="col-span-2 px-2 py-1 text-sm text-gray-500">Aucun produit trouvé.</p>
            )}
          </div>

          {selectedProducts.size === 0 && (
            <p className="mt-2 text-xs text-amber-600">
              Aucun produit sélectionné : tous les produits seront utilisés.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default ProductFilter;
